import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { NAV_CONFIG } from "../data/navigation";
import MegaMenu from "./MegaMenu";
import Icon from "./Icon";
import "../styles/navbar.css";
import "../styles/megaMenu.css";

function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDesktopMenu, setOpenDesktopMenu] = useState(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [openMobileSection, setOpenMobileSection] = useState(null);
  const navRef = useRef(null);
  const menuCloseTimeout = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close everything on route change.
  useEffect(() => {
    setOpenDesktopMenu(null);
    setIsMobileOpen(false);
    setOpenMobileSection(null);
  }, [location]);

  // Close desktop dropdown when clicking outside the navbar.
  useEffect(() => {
    const onClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDesktopMenu(null);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileOpen]);

  const handleItemEnter = (label) => {
    clearTimeout(menuCloseTimeout.current);
    setOpenDesktopMenu(label);
  };

  const handleItemLeave = () => {
    clearTimeout(menuCloseTimeout.current);

    menuCloseTimeout.current = setTimeout(() => {
      setOpenDesktopMenu(null);
    }, 200);
  };
  const handleItemClick = (label, hasMenu) => {
    if (!hasMenu) return;
    setOpenDesktopMenu((current) => (current === label ? null : label));
  };

  return (
    <header className={`nav ${isScrolled ? "nav--scrolled" : ""}`} ref={navRef}>
      <div className="container nav-inner">
        <Link to="/" className="nav-brand" aria-label="MPeoples HRMS home">
          <span className="nav-brand-mark" aria-hidden="true">
            {/* <svg viewBox="0 0 28 28" width="22" height="22" fill="none">
              <rect width="28" height="28" rx="8" fill="#1F52C4" />
              <path d="M8 19V9.5h2.4l3.4 5.4 3.4-5.4H20V19h-2.4v-6l-3.2 5-3.2-5v6H8Z" fill="#fff" />
            </svg> */}
            <img src="/logo.png" alt="HRMS" />
          </span>
          <span className="nav-brand-text">MPeoples HRMS</span>
        </Link>

        <nav className="nav-links" aria-label="Primary">
          {NAV_CONFIG.map((item) => {
            const hasMenu = Boolean(item.menu);
            const isOpen = openDesktopMenu === item.label;
            return (
              <div
                className="nav-item"
                key={item.label}
                onMouseEnter={() => hasMenu && handleItemEnter(item.label)}
                onMouseLeave={() => hasMenu && handleItemLeave()}
              >
                {hasMenu ? (
                  <button
                    type="button"
                    className={`nav-link nav-link--trigger ${isOpen ? "nav-link--open" : ""}`}
                    aria-expanded={isOpen}
                    onClick={() => handleItemClick(item.label, hasMenu)}
                  >
                    {item.label}
                    <Icon
                      name="chevron"
                      size={14}
                      className="nav-link-chevron"
                    />
                  </button>
                ) : (
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "nav-link--active" : ""}`
                    }
                  >
                    {item.label}
                  </NavLink>
                )}

                {hasMenu && isOpen && (
                  <MegaMenu
                    menu={item.menu}
                    onNavigate={() => setOpenDesktopMenu(null)}
                  />
                )}
              </div>
            );
          })}
        </nav>

        <div className="nav-actions">
          <Link to="/contact" className="btn btn-primary nav-cta">
            Request a Demo
          </Link>
          <button
            type="button"
            className={`nav-toggle ${isMobileOpen ? "nav-toggle--open" : ""}`}
            aria-label={isMobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileOpen}
            onClick={() => setIsMobileOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      <div
        className={`nav-mobile-backdrop ${isMobileOpen ? "nav-mobile-backdrop--open" : ""}`}
        onClick={() => setIsMobileOpen(false)}
        aria-hidden="true"
      />

      <div className={`nav-mobile ${isMobileOpen ? "nav-mobile--open" : ""}`}>
        <nav className="nav-mobile-links" aria-label="Mobile">
          {NAV_CONFIG.map((item) => {
            const hasMenu = Boolean(item.menu);
            const isSectionOpen = openMobileSection === item.label;
            return (
              <div className="nav-mobile-group" key={item.label}>
                <div className="nav-mobile-row">
                  <Link
                    to={item.to}
                    className="nav-mobile-link"
                    onClick={() => setIsMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                  {hasMenu && (
                    <button
                      type="button"
                      className={`nav-mobile-toggle ${isSectionOpen ? "nav-mobile-toggle--open" : ""}`}
                      aria-label={`Toggle ${item.label} menu`}
                      aria-expanded={isSectionOpen}
                      onClick={() =>
                        setOpenMobileSection(isSectionOpen ? null : item.label)
                      }
                    >
                      <Icon name="chevron" size={16} />
                    </button>
                  )}
                </div>

                {hasMenu && isSectionOpen && (
                  <div className="nav-mobile-submenu">
                    {item.menu.columns.map((col) => (
                      <div key={col.heading}>
                        <span className="nav-mobile-submenu-heading">
                          {col.heading}
                        </span>
                        {col.items.map((sub) => (
                          <Link
                            key={sub.to}
                            to={sub.to}
                            className="nav-mobile-sublink"
                            onClick={() => setIsMobileOpen(false)}
                          >
                            <Icon name={sub.icon} size={16} />
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <Link
          to="/contact"
          className="btn btn-primary btn-block"
          onClick={() => setIsMobileOpen(false)}
        >
          Request a Demo
        </Link>
      </div>
    </header>
  );
}

export default Navbar;
